import React from 'react';
import { observer } from 'mobx-react';
import { SearchInput, SearchItemProps } from 'ui/standard/input/search_input/search_input';
import { BandwidthStore } from './bandwidth_store';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { BANDWIDTH_UNITS, BandwidthUnit } from 'services/accounts/types';
import baseStyles from './bandwidth_input.module.css';
import { TextInput } from 'ui/public/input';
import Messages from './bandwidth_input_messages';
import { UnreachableFunction } from 'ui/base/types';

const ALWAYS_TRUE = () => true;
const IDENTITY = (item: BandwidthUnit) => item;

export function getBandwidthUnitText(unit: BandwidthUnit): string {
  switch(unit) {
    case BandwidthUnit.MBPS:
      return Messages.mbps();
    case BandwidthUnit.GBPS:
      return Messages.gbps();
    default:
      return UnreachableFunction(unit);
  }
}

type BandwidthInputStyles = {
  container: string;
  bandwidthDisplay: string;
  listItem: string;
};

const themedStyles: ThemedStyles<BandwidthInputStyles> = {
  [Theme.DAYLIGHT]: {
    container: baseStyles.container,
    bandwidthDisplay: baseStyles.bandwidthDisplay,
    listItem: baseStyles.listItem,
  },
  [Theme.MIDNIGHT]: {
    container: baseStyles.container,
    bandwidthDisplay: baseStyles.bandwidthDisplay,
    listItem: baseStyles.listItem,
  },
};

type BandwidthUnitDisplayProps = {
  store: BandwidthStore;
};
const BandwidthUnitDisplay = observer(({ store }: BandwidthUnitDisplayProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.bandwidthDisplay} onClick={store.search.modal.showModal}>
      {getBandwidthUnitText(store.unit.value)}
    </div>
  );
});

type BandwidthUnitItemProps = {
  store: BandwidthStore;
  data: BandwidthUnit;
};
const BandwidthUnitItem = observer(({ data, store }: BandwidthUnitItemProps) => {
  const styles = useStyles(themedStyles);
  const onClick = React.useCallback(() => {
    store.search.modal.hideModal();
    store.unit.setValue(data);
  }, [store, data]);

  return (
    <div className={styles.listItem} onClick={onClick}>
      {getBandwidthUnitText(data)}
    </div>
  );
});

type BandwidthUnitInputProps = {
  store: BandwidthStore;
};
const BandwidthUnitInput = observer(({ store }: BandwidthUnitInputProps) => {
  const DisplayComponent = React.useCallback(() => {
    return <BandwidthUnitDisplay store={store}/>;
  }, [store]);

  const ItemComponent = React.useCallback(({ data }: SearchItemProps<BandwidthUnit>) => {
    return <BandwidthUnitItem store={store} data={data}/>;
  }, [store]);

  return (
    <SearchInput
      DisplayComponent={DisplayComponent}
      ItemComponent={ItemComponent}
      data={BANDWIDTH_UNITS}
      filterFunc={ALWAYS_TRUE}
      keyFunc={IDENTITY}
      placeholder={getBandwidthUnitText(BandwidthUnit.MBPS)}
      store={store.search}
    />
  );
});

type BandwidthInputProps = {
  store: BandwidthStore;
};
export const BandwidthInput = observer(({ store }: BandwidthInputProps) =>{
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.container}>
      <TextInput store={store.string}/>
      <BandwidthUnitInput store={store}/>
    </div>
  );
});
